import { Injectable } from '@angular/core';
import {
  IDispositivo,
  IDispositivoRepresa,
  IQueryParam,
  IReporte,
  IReporteRepresa,
} from 'modelos/src';
import { HelperService } from 'src/app/auxiliares/helper.service';
import { apiBase } from 'src/environments/environment';
import { HttpService } from '../../../auxiliares/http.service';

const api = apiBase;

@Injectable({
  providedIn: 'root',
})
export class RepresasService {
  constructor(private http: HttpService) {}

  public frecReporte(deveui: string, frecuencia: number) {
    return this.http.post(`${api}/comandos/canal/frecReporte`, {
      deveui: deveui,
      frecuencia,
    });
  }

  // Helper

  private fechaAyer() {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 1);
    return fecha;
  }

  public estadoDispositivo(dato: IDispositivo) {
    const ayer = this.fechaAyer();
    const ultimoReporte = dato.ultimoReporte?.fecha
      ? new Date(dato.ultimoReporte.fecha)
      : null;
    const ultimaAlerta = dato.ultimaAlerta?.fecha
      ? new Date(dato.ultimaAlerta.fecha)
      : null;
    if (ultimaAlerta) {
      if (!ultimoReporte || ultimaAlerta > ultimoReporte) {
        return 'Alerta';
      }
    }
    if (!ultimoReporte || ayer > ultimoReporte) {
      return 'Sin reportar';
    }
    return 'Ok';
  }

  static estadoDispositivo2(dato: IDispositivo) {
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);

    const ultimoReporte = dato.ultimoReporte?.fecha
      ? new Date(dato.ultimoReporte.fecha)
      : null;
    const ultimaAlerta = dato.ultimaAlerta?.fecha
      ? new Date(dato.ultimaAlerta.fecha)
      : null;
    if (ultimaAlerta) {
      if (!ultimoReporte || ultimaAlerta > ultimoReporte) {
        return `<span class="material-icons" style="color: red">error</span>`;
      }
    }
    if (!ultimoReporte || ayer > ultimoReporte) {
      return `<span class="material-icons" style="color: red">error</span>`;
    }
    return `<span class="material-icons" style="color: green">check_circle</span>`;
  }

  public calcularAltura(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const r = (reporte?.reporte ||
      dispositivo?.ultimoReporte?.reporte) as IReporteRepresa;
    const distancia = r?.distancia || 0;
    const cota = config?.igm;
    if (r) {
      if (cota) {
        return distancia - cota;
      } else {
        return distancia;
      }
    }
  }

  public calcularNivel(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const altura = this.calcularAltura(dispositivo, reporte);
    if (altura) {
      const nivel = +(altura / 1000).toFixed(1);
      return nivel;
    }
  }

  static calcularAltura2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const r = (reporte?.reporte ||
      dispositivo?.ultimoReporte?.reporte) as IReporteRepresa;
    const distancia = r?.distancia || 0;
    const cota = config?.igm;
    if (r) {
      if (cota) {
        return distancia - cota;
      } else {
        return distancia;
      }
    }
  }

  static calcularNivel2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const altura = this.calcularAltura2(dispositivo, reporte);
    if (altura) {
      const nivel = +(altura / 1000).toFixed(1);
      return nivel;
    }
  }

  public calcularVolumen(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const nivel = this.calcularNivel(dispositivo, reporte);
    if (nivel) {
      const m3 = config?.niveles?.[nivel]?.m3;
      return m3 || 0;
    }
  }

  static calcularVolumen2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const nivel = this.calcularNivel2(dispositivo, reporte);
    if (nivel) {
      const m3 = config?.niveles?.[nivel]?.m3;
      return m3 || 0;
    }
  }

  public calcularPorcentaje(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const nivel = this.calcularNivel(dispositivo, reporte);
    if (nivel) {
      const porc = config?.niveles?.[nivel]?.porcentaje;
      return porc || 0;
    }
  }
  static calcularPorcentaje2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const nivel = this.calcularNivel2(dispositivo, reporte);
    if (nivel) {
      const porc = config?.niveles?.[nivel]?.porcentaje;
      return porc || 0;
    }
  }

  public calcularHectareas(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const volumen = this.calcularVolumen(dispositivo, reporte);
    if (volumen) {
      const hectareas = (volumen / 20000) * 1.7424;
      return hectareas;
    }
  }

  static calcularHectareas2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const volumen = this.calcularVolumen2(dispositivo, reporte);
    if (volumen) {
      const hectareas = (volumen / 20000) * 1.7424;
      return hectareas;
    }
  }

  public calcularHorasDisponibles(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const volumen = this.calcularVolumen(dispositivo, reporte);
    const hectareas = this.calcularHectareas(dispositivo, reporte);
    const consumoPorHora = config?.consumoPorHora;
    if (volumen && consumoPorHora) {
      const horasDisponibles = volumen / consumoPorHora;
      return +horasDisponibles.toFixed(0) || 0;
    }
  }

  static calcularHorasDisponibles2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const config = dispositivo?.config as IDispositivoRepresa;
    const volumen = this.calcularVolumen2(dispositivo, reporte);
    const hectareas = this.calcularHectareas2(dispositivo, reporte);
    const consumoPorHora = config?.consumoPorHora;
    if (volumen && consumoPorHora) {
      const horasDisponibles = volumen / consumoPorHora;
      return +horasDisponibles.toFixed(0) || 0;
    }
  }

  public calcularDiasDisponibles(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const horasDisponibles = this.calcularHorasDisponibles(
      dispositivo,
      reporte
    );
    if (horasDisponibles) {
      return +(horasDisponibles / 24).toFixed(0);
    }
  }
  static calcularDiasDisponibles2(
    dispositivo?: IDispositivo,
    reporte?: IReporte
  ): number | void {
    const horasDisponibles = this.calcularHorasDisponibles2(
      dispositivo,
      reporte
    );
    if (horasDisponibles) {
      return +(horasDisponibles / 24).toFixed(0);
    }
  }

  public calcularConsumo(dispositivo?: IDispositivo): number {
    const config = dispositivo?.config as IDispositivoRepresa;
    return config?.consumoPorHora || 0;
  }
  static calcularConsumo2(dispositivo?: IDispositivo): number {
    const config = dispositivo?.config as IDispositivoRepresa;
    return config?.consumoPorHora || 0;
  }

  public async exportarReportes(
    queryParams?: IQueryParam,
    filename = 'reportes.xlsx'
  ): Promise<void> {
    let params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(`${api}/reportes/exportar/canal`, filename, params);
  }
}
